'use client';

import classNames from '@sindresorhus/class-names';
import Star from '@web-catalog/public/img/star.svg';
import Image from 'next/image';
import Link from 'next/link';
import {useCallback, useRef} from 'react';

import {MINIMUM_FOR_RATING} from '@web-catalog/const/numbers';

import {AppsSelectionContent, SelectionApp} from '@web-catalog/types';

import {useOnScreenOnce} from '@web-catalog/hooks';

import {ROUTES} from '@web-catalog/routes';

import {Price} from '@web-catalog/ui-kit/Price';
import {Text} from '@web-catalog/ui-kit/Text';

import {useFeaturingAnalytics} from '@web-catalog/utils/analytics/useFeaturingAnalytics';
import {formatRating} from '@web-catalog/utils/formatRating';

import styles from './AppsListComponent.module.scss';
import {PreorderLink} from './PreorderLink/PreorderLink';

export const AppsListComponent = ({
  content,
  featuringName,
  featuringId,
  featuringType,
  isEnablePreorderRedesign,
}: {
  content: AppsSelectionContent & {type: 'APPS'};
  featuringName?: string;
  featuringId: string;
  featuringType?: string;
  isEnablePreorderRedesign?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const {logAppSelect, logCompilationShow} = useFeaturingAnalytics();
  const isPreorderApp = content?.appInfo?.[0]?.preorderStatus;

  useOnScreenOnce({
    ref,
    intersectionThreshold: 0.5,
    callback: useCallback(() => {
      logCompilationShow(
        featuringId,
        featuringName,
        isPreorderApp ? 'grid' : featuringType,
      );
    }, [logCompilationShow, featuringId, featuringName, featuringType]),
  });

  const handleAppClickAnalytics = useCallback(
    (app: SelectionApp, index: number) => {
      logAppSelect(app, index, featuringId, featuringName, featuringType);
    },
    [logAppSelect, featuringId, featuringName, featuringType],
  );

  return (
    <div
      className={classNames(styles.apps, {
        [styles.apps__preorders]: isPreorderApp && !isEnablePreorderRedesign,
      })}
      ref={ref}
    >
      {content?.appInfo?.map((elem, index) => {
        const reviewsCount =
          Number(elem.totalRatings) >= MINIMUM_FOR_RATING
            ? elem.totalRatings
            : 0;
        const averageUserRating = reviewsCount ? elem.averageUserRating : 0;

        if (!elem.preorderStatus)
          return (
            <Link
              key={elem.appId}
              href={ROUTES.app.app(elem.packageName)}
              className={classNames(styles.app, {
                [styles.single]: content.appInfo.length === 1,
                [styles.redesign]: isEnablePreorderRedesign,
              })}
              target={'_blank'}
              onClick={() => handleAppClickAnalytics(elem, index + 1)}
            >
              <div className={styles.icon}>
                <Image src={elem.iconUrl} alt={elem.appName} fill />
              </div>

              <div>
                <div className={styles.appName}>{elem.appName}</div>
                <p className={styles.appDescription}>{elem.shortDescription}</p>
                {!!(elem.price && elem.price > 0) && (
                  <Price className={styles.appPrice} price={elem.price} />
                )}
                {!!elem.averageUserRating && !!elem.totalRatings && (
                  <span
                    itemScope
                    itemType="https://schema.org/AggregateRating"
                    itemProp="aggregateRating"
                    className={styles.rating}
                  >
                    <meta
                      itemProp="ratingValue"
                      content={`${averageUserRating}`}
                    />
                    <meta itemProp="reviewCount" content={`${reviewsCount}`} />
                    <Star />
                    <Text typography="content-s" color="neutral-500">
                      {formatRating(Number(averageUserRating || 0))}
                    </Text>
                  </span>
                )}
              </div>
            </Link>
          );

        return (
          <PreorderLink
            featuringId={featuringId}
            key={elem.appId}
            isEnablePreorderRedesign={isEnablePreorderRedesign}
            {...elem}
            placement="monoFeaturing"
          />
        );
      })}
    </div>
  );
};
